// What other parts put in a detail's header (`defineScreenPart({
// headerActions })`), in part order: their buttons before the star, and their
// items of the detail's "…". The detail names its kind and the thing; it knows
// nothing of what they do.
//
// Promise:
// - Choosing an action's item opens it (`opened`); what it shows while opened
//   (a dialog) is its own. Which one is open is screen state, never in the URL.
import { type ReactNode, useState } from "react";
import { useMeApp } from "../app/context";
import type { RowNoteTarget } from "../app/screen-part";
import type { MenuItem } from "../ui/menu";
import type { ShellActionInput } from "./outside/outside";

export function useHeaderActions(
  kind: string,
  targetId: string,
  ext: Readonly<Record<string, unknown>> | undefined,
): {
  buttons: ReactNode;
  /** The same buttons as header data, in part order (shell/outside). */
  actions: ShellActionInput[];
  menuItems: MenuItem[];
} {
  const all = useMeApp().headerActions[kind] ?? [];
  const [opened, setOpened] = useState<string | null>(null);
  const menuItems: MenuItem[] = [];
  for (const a of all) {
    const item = a.menuItem?.({ targetId, ext });
    if (item)
      menuItems.push({
        id: `action:${a.id}`,
        label: item.label,
        onSelect: () => setOpened(a.id),
      });
  }
  const actions: ShellActionInput[] = [];
  for (const a of all) {
    // The parts' list is the app's for its whole life: the hooks keep order.
    // biome-ignore lint/correctness/useHookAtTopLevel: the same hooks in the same order on every render
    const data = a.useAction?.({ targetId, ext }) ?? null;
    const { id, Component } = a;
    if (!Component) continue;
    const drawn = (
      <Component
        key={id}
        targetId={targetId}
        ext={ext}
        opened={opened === id}
        open={() => setOpened(id)}
        close={() => setOpened((o) => (o === id ? null : o))}
      />
    );
    actions.push(
      data
        ? {
            id: `action:${id}`,
            label: data.label,
            ...(data.icon ? { icon: data.icon } : {}),
            onPress: data.onPress ?? (() => setOpened(id)),
            render: () => drawn,
          }
        : { id: `action:${id}`, label: "", webOnly: true, render: () => drawn },
    );
  }
  const buttons = <>{actions.map((a) => a.render?.(false))}</>;
  return { buttons, actions, menuItems };
}

/**
 * What other parts put on a list's row (`defineScreenPart({ rowMarks })`), in
 * part order: their marks right after the name (`place` "mark", inside the
 * row's link) or their buttons at the name column's end ("button", in the
 * row's `aside`).
 */
export function RowMarks({
  kind,
  place,
  targetId,
  ext,
  round,
}: {
  kind: string;
  place: "mark" | "button";
  targetId: string;
  ext: Readonly<Record<string, unknown>> | undefined;
  /** The buttons are the row's 26 px round ones (`RowButtonProps.round`). */
  round?: boolean;
}) {
  const marks = useMeApp().rowMarks?.[kind] ?? [];
  return (
    <>
      {marks.map(({ id, Mark, Button }) =>
        place === "mark" ? (
          Mark ? (
            <Mark key={id} targetId={targetId} ext={ext} />
          ) : null
        ) : Button ? (
          <Button key={id} targetId={targetId} ext={ext} round={round} />
        ) : null,
      )}
    </>
  );
}

/**
 * The words other parts put at the head of a row's line under its title
 * (`RowMark.useNote`), in part order, keyed by the mark's id; only those that
 * say something, so the row knows whether its line has anything.
 */
export function useRowNotes(
  kind: string,
  target: RowNoteTarget,
): { id: string; node: ReactNode }[] {
  const marks = useMeApp().rowMarks?.[kind] ?? [];
  const out: { id: string; node: ReactNode }[] = [];
  for (const { id, useNote } of marks) {
    // biome-ignore lint/correctness/useHookAtTopLevel: the marks are fixed for the app's life, so the hooks run in the same order on every render
    const node = useNote?.(target);
    if (node !== null && node !== undefined && node !== false)
      out.push({ id, node });
  }
  return out;
}
